/* istanbul ignore file */
import { removeBrandFromName, commaSeparate } from '@/utils/General';
import { getPrice, buildKey, formatPrice } from '@/utils/returns/General';

const createProductObject = (product = {}, page, inlineSizeAndColor) => ({
  id: product.itemDetails.productId,
  key: buildKey(product),
  virtualReturnItem: product.returnType === 'VR' || product.returnType === 'VE',
  virtualReturnType: product.returnType,
  returnQuantity: product.returnQuantity,
  returnWindowDate: product.itemDetails.returnWindowDate,
  returnEligibleFlag: product.itemDetails.returnEligibleFlag ?? false,
  inTransit: product.inTransit ?? false,
  upc: product.upc,
  returnReason: product.returnReason,
  mosFlag: product.mosFlag,
  virtualReturnExpectedBackDate: product.virtualReturnExpectedBackDate,
  lineNumber: product.orderLineId,
  isHazmatItem: product.isHazmatItem,
  imagery: {
    urlTemplate: product.itemDetails.urlTemplate,
    primaryImage: {
      filePath: product.itemDetails.primaryImageFilePath,
    },
  },
  identifier: {
    productUrl: product.itemDetails.productId ? `/shop/product/?ID=${product.itemDetails.productId}` : undefined,
  },
  detail: {
    name:
      removeBrandFromName(product.itemDetails.productBrand, product.itemDetails.productName) ||
      'There was an error loading the item information. Try again later',
    brand: product.itemDetails.productBrand || (product.productName ? 'Brand Name' : 'Item Name'),
    gmm: product.itemDetails.productGmm,
    ...(inlineSizeAndColor && {
      colorAndSize: [commaSeparate(product.itemDetails.upcSize, product.itemDetails.upcColor)],
    }),
    ...(!inlineSizeAndColor && {
      colorAndSize: [product.itemDetails.upcSize, product.itemDetails.upcColor],
    }),
    quantity: product.returnQuantity,
    upcNumber: product.itemDetails.upcNumber,
    orderToken: product.orderToken,
    shipmentToken: product.shipmentToken,
    productUrl: product.itemDetails.productId ? `/shop/product/?ID=${product.itemDetails.productId}` : undefined,
  },
  price: [
    {
      values: [
        {
          value: page === 'returns' ? getPrice(formatPrice(product.unitPrice), false) : getPrice(formatPrice(product.itemTotal), false),
          formattedValue: page === 'returns' ? formatPrice(product.unitPrice) : formatPrice(product.itemTotal),
        },
      ],
    },
  ],
  ...(!inlineSizeAndColor && {
    traits: {
      colors: [product.itemDetails.upcColor],
      size: [product.itemDetails.upcSize],
    },
  }),
  pricing: {
    price: {
      tieredPrice: [
        {
          label: '[PRICE]',
          values: [
            {
              type: 'regular',
              value: page === 'returns' ? getPrice(formatPrice(product.unitPrice), false) : getPrice(formatPrice(product.itemTotal), false),
              formattedValue: page === 'returns' ? formatPrice(product.unitPrice) : formatPrice(product.itemTotal),
            },
          ],
        },
      ],
    },
  },
});

export const transformProduct = (product = {}, isMarketplaceOrder, page, inlineSizeAndColor) => {
  if (!product.itemDetails) product.itemDetails = {};

  const transformedProduct = createProductObject(product, page, inlineSizeAndColor);

  if (product.itemDetails.mkpLineItemDetails && isMarketplaceOrder) {
    transformedProduct.mkpLineItemDetails = product.itemDetails.mkpLineItemDetails;
  }

  if (product.itemDetails.returnPolicyDays) {
    transformedProduct.returnPolicyDays = product.itemDetails.returnPolicyDays;
  }

  if (product.lastActIndicator || product.itemDetails.lastActIndicator) {
    transformedProduct.lastActIndicator = product.lastActIndicator || product.itemDetails.lastActIndicator;
  }

  if (product.itemDetails.isReturnUnallowedFlag) {
    transformedProduct.isReturnUnallowedFlag = product.itemDetails.isReturnUnallowedFlag;
  }

  if (product.shipmentStatusCode) {
    transformedProduct.shipmentStatusCode = product.shipmentStatusCode;
  }

  if (product.shipmentType) {
    transformedProduct.shipmentType = product.shipmentType;
  }

  if (product.itemDetails.withProtectionPlan) {
    Object.assign(transformedProduct, {
      withProtectionPlan: product.itemDetails.withProtectionPlan,
      planDesc: product.itemDetails.planDesc,
      planProviderName: product.itemDetails.planProviderName,
      protectionPlanPrice: page == 'return-details' ? product.itemDetails.lineProtectionPlanTotal : product.itemDetails.protectionPlanPrice,
      protectionPlanStatus: product.itemDetails.protectionPlanStatus,
    });
  }

  return transformedProduct;
};

const transformProducts = (data = {}) => {
  const { page, products, isMkpOrder, inlineSizeAndColor } = data;
  if (!products || !products.length) return [];
  return products.map((product) => transformProduct(product, isMkpOrder, page, inlineSizeAndColor));
};

export const transformProductFromPrepareDeclaration = (cartonLine = {}) => {
  const selectedUpc = cartonLine.upcSubstitutionMap[cartonLine.upc] || {};
  const selectedProduct = cartonLine.productMap[selectedUpc.productId] || {};
  selectedUpc.price = cartonLine.priceMap[selectedUpc.priceId] || {};
  selectedUpc.color = cartonLine.colorMap[selectedUpc.colorId] || {};
  selectedUpc.size = cartonLine.sizeMap[selectedUpc.sizeId] || {};
  const primaryImage = selectedUpc.color.imagery?.find((img) => img.primary) || {};
  return {
    id: cartonLine.productId,
    upc: cartonLine.upc,
    detail: {
      name: removeBrandFromName(selectedProduct.productBrand, selectedProduct.productName),
      brand: selectedProduct.productBrand,
      colorAndSize: [commaSeparate(selectedUpc.size.name, selectedUpc.color.color)],
      upcNumber: cartonLine.upc,
    },
    imagery: {
      primaryImage: {
        filePath: primaryImage.filePath,
        name: primaryImage.name,
      },
    },
    pricing: {
      price: {
        tieredPrice: [
          {
            label: '[PRICE]',
            values: [
              {
                type: 'regular',
                value: formatPrice(+selectedUpc.price.value),
                formattedValue: formatPrice(+selectedUpc.price.formattedValue),
              },
            ],
          },
        ],
      },
    },
  };
};

export default transformProducts;
